import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    signal,
    viewChild,
    ChangeDetectionStrategy,
} from "@angular/core"
import {DialogSize} from "@app/common/models/dialogs"
import {CMConfiguratorElement} from "@app/common/components/viewers/configurator/types/cm-configurator"
import {CM_LOGGER_AR} from "@app/common/services/logging"
import {TestCaseEntry, updateIframe} from "@app/test/manual-testing/helpers/configurator-test-helpers"
import {getBasicPage, getDelvauxStylePage, getPdfButtonPage, getStylingPage} from "@app/test/manual-testing/helpers/webcomponent-pages"
import {TestContainerComponent} from "@app/test/manual-testing/test-container/test-container.component"
import {configuratorWcModuleUrls, Source} from "@app/test/manual-testing/types/types"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-configurator-test-wc",
    templateUrl: "./configurator-test-wc.component.html",
    styleUrls: ["./configurator-test-wc.component.scss"],
    imports: [TestContainerComponent],
})
export class ConfiguratorTestWCComponent implements AfterViewInit {
    readonly $source = input.required<Source>({alias: "source"})

    private readonly $testContainer = viewChild.required<TestContainerComponent>("testContainer")
    private readonly $iframe = viewChild.required<ElementRef<HTMLIFrameElement>>("configuratorIframe")

    dialogSizes = DialogSize
    readonly $showOverlay = signal(false)
    readonly changeDetectorRef = inject(ChangeDetectorRef)
    private readonly arLogger = inject(CM_LOGGER_AR)

    readonly $moduleUrl = computed(() => configuratorWcModuleUrls[this.$source()])

    testCases: TestCaseEntry[] = [
        {
            type: "info",
            name: "What you should see",
            tooltip: "A template, loaded by specifying the template-uuid attribute on the web component. Take a snapshot.",
            fct: () => {},
        },
        {
            type: "test",
            name: "Pixel ratio",
            tooltip: "Configurator with pixel ratio 2, take snapshot and compare the size to the previous snapshot. It should be twice as large.",
            fct: () => {
                const _doc = this.$iframe().nativeElement.contentDocument
                updateIframe(this.$iframe().nativeElement, getBasicPage(this.$moduleUrl(), "template-uuid=1499ef45-504e-4782-bdf1-3175f207a96f pixel-ratio=2"))
            },
        },
        {
            type: "test",
            name: "Initial parameters",
            tooltip: "Load a template with initial parameters set on the web component. The same chair, but it should display a different configuration.",
            fct: () => {
                const _doc = this.$iframe().nativeElement.contentDocument
                updateIframe(
                    this.$iframe().nativeElement,
                    getBasicPage(
                        this.$moduleUrl(),
                        "template-uuid=1499ef45-504e-4782-bdf1-3175f207a96f parameters=param(4d201a9a-81b1-45d1-a4bc-a7c36121a0e7)=config(fd256de2-c0e3-4ef3-98e9-25ffa7e800ff)&param(0fb20bb9-2284-4a8b-b6e0-db02ebc36742%2Fced9d80c-7103-43f2-a5ba-2d90e0c823b3)=config(ec17fd8c-6131-46d8-8bdd-a2ac0f9d29a6)&param(0fb20bb9-2284-4a8b-b6e0-db02ebc36742%2F60a5a88a-7701-4c10-819e-deb5f87f15c4)=config(c93e318c-88ee-4e8c-9b56-9c7075f53784)",
                    ),
                )
            },
        },
        {
            type: "test",
            name: "Loading by article id",
            tooltip: "Load a template with article id / organization id",
            fct: () => {
                const _doc = this.$iframe().nativeElement.contentDocument
                updateIframe(
                    this.$iframe().nativeElement,
                    getBasicPage(this.$moduleUrl(), "organization-uuid=32d8ab28-c112-4fd7-be10-065e4875fdfb article-id=51e649ba04fd"),
                )
            },
        },
        {
            type: "test",
            name: "Hiding the ui",
            tooltip: "The menu and action menu should disappear.",
            fct: () => {
                const _doc = this.$iframe().nativeElement.contentDocument
                updateIframe(this.$iframe().nativeElement, getBasicPage(this.$moduleUrl(), "template-uuid=1499ef45-504e-4782-bdf1-3175f207a96f show-ui=false"))
            },
        },
        {
            type: "test",
            name: "Switch template",
            tooltip: `Switch to a page that shows all styling options.`,
            fct: () => {
                const _doc = this.$iframe().nativeElement.contentDocument
                updateIframe(this.$iframe().nativeElement, getStylingPage(this.$moduleUrl(), "accordion", "manualClose", true, false))
            },
        },
        {
            type: "info",
            name: "Fullscreen menu",
            tooltip: `Toggle fullscreen and check if the internal menu appears. Note that this will put the iframe in fullscreen mode and not only the web component's element,
            so the external menu is still visible. This is normal in this test case, because toggling checks for iframe presence - usually the web components are not
            displayed in an iframe.`,
            fct: () => {},
        },
        {
            type: "info",
            name: "Manual close, captions",
            tooltip: "Opening multiple menu items should keep them open",
            fct: () => {},
        },
        {
            type: "info",
            name: "Styling",
            tooltip: "The current configurator uses all styling options. Check - TODO: Maybe add some images and more detailed hints on what to look for.",
            fct: () => {},
        },
        {
            type: "test",
            name: "Auto close",
            tooltip: `Reload the page and check if the external menu items now close automatically.`,
            fct: () => {
                const _doc = this.$iframe().nativeElement.contentDocument
                updateIframe(this.$iframe().nativeElement, getStylingPage(this.$moduleUrl(), "accordion", "autoClose", true, false))
            },
        },
        {
            type: "test",
            name: "Clear parameters",
            tooltip: `Change a few options in the external menu, then click this. The configurator should reset to the configuration it was initially loaded with, and the menu's selection/open sections should reset too.`,
            fct: () => {
                const doc = this.$iframe().nativeElement.contentDocument
                const configurator = doc?.querySelector("cm-configurator-main") as CMConfiguratorElement
                configurator.clearParameters()
            },
        },
        {
            type: "test",
            name: "Icons, no captions",
            tooltip: `Reload the page and check if the external menu displays as icons without captions`,
            fct: () => {
                const _doc = this.$iframe().nativeElement.contentDocument
                updateIframe(this.$iframe().nativeElement, getStylingPage(this.$moduleUrl(), "icons", "autoClose", false, false))
            },
        },
        {
            type: "test",
            name: "Delvaux style menu",
            tooltip: `Reload the page with the Delvaux sample's step-through accordion menu styling (centered titles, back/forward chevrons, captioned icons).`,
            fct: () => {
                const _doc = this.$iframe().nativeElement.contentDocument
                updateIframe(this.$iframe().nativeElement, getDelvauxStylePage(this.$moduleUrl()))
            },
        },
        {
            type: "test",
            name: "Pdf button",
            tooltip: `Load a template with pdf enabled, then check: The button web component, the pdf button inside the configurator and prices inside the pdf.
            The pdfDownloadTriggered counter below the button must increment on every click of either button.`,
            fct: () => {
                const _doc = this.$iframe().nativeElement.contentDocument
                updateIframe(this.$iframe().nativeElement, getPdfButtonPage(this.$moduleUrl()))
            },
        },
        {
            type: "test",
            name: "In-memory snapshot",
            tooltip: `After clicking, an image should be shown on the bottom of the page (click image to close).`,
            fct: () => {
                const doc = this.$iframe().nativeElement.contentDocument
                const configurator = doc?.querySelector("cm-configurator-main") as CMConfiguratorElement
                configurator.captureSnapshotInMemory().then((snapshot) => {
                    this.$testContainer().setImage(snapshot)
                    this.$showOverlay.set(true)
                })
            },
        },
        {
            type: "test",
            name: "In-memory snapshot (transparent)",
            tooltip: `Like the in-memory snapshot, but requests a transparent PNG. The shown image should have no background (the page/overlay backdrop shows through), even on scenes with a configured background color/image.`,
            fct: () => {
                const doc = this.$iframe().nativeElement.contentDocument
                const configurator = doc?.querySelector("cm-configurator-main") as CMConfiguratorElement
                configurator.captureSnapshotInMemory("image/png", undefined, true).then((snapshot) => {
                    this.$testContainer().setImage(snapshot)
                    this.$showOverlay.set(true)
                })
            },
        },
        {
            type: "test",
            name: "Save snapshot (transparent)",
            tooltip: `Downloads a transparent PNG snapshot (scene background color/image is ignored).`,
            fct: () => {
                const doc = this.$iframe().nativeElement.contentDocument
                const configurator = doc?.querySelector("cm-configurator-main") as CMConfiguratorElement
                configurator.saveSnapshot("image/png", undefined, true)
            },
        },
        {
            type: "test",
            name: "Save snapshot (custom file name)",
            tooltip: `Downloads a PNG snapshot with a hostile file name. The download must succeed with a sanitized file name`,
            fct: () => {
                const doc = this.$iframe().nativeElement.contentDocument
                const configurator = doc?.querySelector("cm-configurator-main") as CMConfiguratorElement
                configurator.saveSnapshot("image/png", undefined, false, 'CON: a/b\\c<d>e|f"g?h*i  ')
            },
        },
        {
            type: "test",
            name: "AR api",
            tooltip: `The web component listens for the AR url and generates its own QR code. This is displayed in a separate overlay  (click image to close).`,
            fct: () => {
                const doc = this.$iframe().nativeElement.contentDocument
                const configurator = doc?.querySelector("cm-configurator-main") as CMConfiguratorElement
                configurator.addEventListener("arUrl", (event) => {
                    this.arLogger.log("arUrl", event.detail)
                    configurator.generateQrCode(event.detail, "low", 200, 0).then((snapshot) => {
                        const _imgElement = document.querySelector(".cm-image-overlay img") as HTMLImageElement
                        this.$testContainer().setImage(snapshot)
                        this.$showOverlay.set(true)
                        this.changeDetectorRef.detectChanges() //TODO: Check if this is still necessary after Angular 17
                    })
                })
                configurator.viewInAr()
            },
        },
        {
            type: "test",
            name: "Mobile test",
            tooltip: `Use the QR code to open the configurator on a mobile device and click around. This is not web-component specific, it is just placed here for convenience.`,
            fct: () => {
                const doc = this.$iframe().nativeElement.contentDocument
                const configurator = doc?.querySelector("cm-configurator-main") as CMConfiguratorElement
                configurator.generateQrCode("https://configurator.colormass.com/?sceneId=955", "low", 200, 0).then((snapshot) => {
                    this.$testContainer().setImage(snapshot)
                    this.$showOverlay.set(true)
                })
            },
        },
        {
            type: "test",
            name: "Load Hotstamping template",
            tooltip: "Load the hotstamping template for Delvaux, you should see a DC font.",
            fct: () => {
                const _doc = this.$iframe().nativeElement.contentDocument
                updateIframe(
                    this.$iframe().nativeElement,
                    getBasicPage(
                        this.$moduleUrl(),
                        "ui-style=default use-external-menu=true parameters=param(font/label)=string(DC)&param(font/material)=string(gold) organization-uuid=314aa687-762a-47ad-9ecb-ab91f2101965 article-id=AB0669ADW0ADNDO pixel-ratio=2",
                    ),
                )
            },
        },
    ]

    ngAfterViewInit() {
        this.loadDefaultConfigurator()
    }

    constructor() {
        effect(() => {
            this.loadDefaultConfigurator()
        })
    }

    private loadDefaultConfigurator() {
        const _doc = this.$iframe().nativeElement.contentDocument
        updateIframe(this.$iframe().nativeElement, getBasicPage(this.$moduleUrl(), "template-uuid=1499ef45-504e-4782-bdf1-3175f207a96f"))
    }
}
